import React from 'react';
import { Image as ImageIcon } from 'lucide-react';

interface PreviewCardProps {
  title: string;
  description: string;
  artworkUrl: string | null;
  episodeDate?: string;
}

export const PreviewCard: React.FC<PreviewCardProps> = ({
  title,
  description,
  artworkUrl,
  episodeDate,
}) => {
  // Format episode date for display below artwork
  const formatEpisodeDate = () => {
    if (episodeDate) {
      const date = new Date(episodeDate);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }
    const today = new Date();
    return today.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const rawTitle = title || 'Episode Title';
  const displayTitle = rawTitle.length > 70 ? `${rawTitle.slice(0, 70)}...` : rawTitle;
  const displayDescription = description || 'No description yet';

  return (
    <div className="border-2 border-cyan-500/50 w-[300px] h-[550px] rounded-lg mt-6 overflow-hidden flex flex-col">
      {/* Top section - dark gray background, artwork centered */}
      <div className="bg-[var(--pm-bg-card)] flex items-center justify-center p-6">
        <div className="aspect-square rounded-lg overflow-hidden w-full max-w-[200px]">
          {artworkUrl ? (
            <img
              src={artworkUrl}
              alt="Episode artwork"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[var(--pm-bg-card-alt)]">
              <ImageIcon className="h-12 w-12 text-[var(--pm-text-faint)]" />
            </div>
          )}
        </div>
      </div>

      {/* Bottom section - black background with text content */}
      <div className="bg-[var(--pm-bg-deeper)] flex-1 p-6">
        {/* Episode date - small gray text */}
        <p className="text-xs text-[var(--pm-text-faint)] mb-2">{formatEpisodeDate()}</p>

        {/* Episode title - bold white */}
        <h2 className="text-lg font-bold text-[var(--pm-text-primary)] mb-3 leading-tight">
          {displayTitle}
        </h2>

        {/* Episode description - gray text */}
        <p className="text-sm text-[var(--pm-text-faint)] leading-relaxed">
          {displayDescription}
        </p>
      </div>
    </div>
  );
};
